import { notification } from "antd";
import _ from "lodash";

export const INDEX_TOLERANCE = 20;
const DEBUG = false;

export const LEAFLET_SCALE = 16;
export const LEAFLET_SESSION_GAP = 9;

export const transformConfig = (railImageConfig, railsFlipped) => {
    let config = railImageConfig.inspection_images;

    if (_.findIndex(config, { showInModal: true }) > -1) {
        config = config.filter((imageConfig) => !imageConfig.showInModal);
    }
    if (railsFlipped) {
        config = _.clone(config);
        config.reverse();
    }

    return config;
};

export const getDirectionLetter = (leftRightConfig, arrayIndex, coordToCompare) => {
    const configItem = leftRightConfig[arrayIndex];

    let textToDisplay = coordToCompare === -1 ? "L" : "R";
    if (configItem && configItem.top === "right") {
        textToDisplay = coordToCompare === -1 ? "R" : "L";
    }
    return textToDisplay;
};

export const getSecondaryArrayIndex = (
    arrayIndexCoord,
    secondaryAlignment,
    arrayOffset,
    primaryReversed,
    secondaryReversed,
    constantArrayOffset,
    manualOffset,
    where,
) => {
    const primaryIndex = (primaryReversed ? 1 : -1) * arrayIndexCoord + arrayOffset + constantArrayOffset;
    const secondaryIsMirrored = primaryReversed !== secondaryReversed;

    //this should never happen but prevents a crash
    if (!secondaryAlignment) return primaryIndex * (secondaryIsMirrored ? -1 : 1);

    const mirrorOffset = (primaryIndex - secondaryAlignment.centreIndex) * (secondaryIsMirrored ? -1 : 1) + secondaryAlignment.centreIndex;

    const arrayIndex = mirrorOffset + secondaryAlignment.inter + (manualOffset >= 0 ? Math.floor(manualOffset) : Math.ceil(manualOffset));

    if (DEBUG && arrayIndexCoord > -2 && arrayIndexCoord < 2) {
        console.log(
            "Debug inter getSecondaryArrayIndex: ",
            where,
            arrayIndexCoord,
            secondaryAlignment,
            arrayOffset,
            primaryReversed,
            constantArrayOffset,
            manualOffset,
            arrayIndex,
        );
    }

    return arrayIndex;
};

export const calculateInterTileDifference = (currentTile, secondaryTiles, centreIndex, railImages, railArrayOffset) => {
    if (!currentTile || !secondaryTiles) return null;

    if (DEBUG) {
        console.log("calculate inter tile called!");
    }

    const { elr, trid, yard: primaryYard, mile: primaryMile } = currentTile.mwv;
    const primaryPosition = parseFloat(primaryMile) * 80 + parseFloat(primaryYard) / 22;

    const secondaryImages = secondaryTiles.data;
    let closestIndex = null;
    let minDistance = Infinity;

    for (let i = 0; i < secondaryImages.length; i++) {
        const { elr: secElr, trid: secTrid, mile: secondaryMile, yard: secondaryYard } = secondaryImages[i].mwv;
        if (secElr !== elr || secTrid !== trid) continue;

        const secondaryPosition = parseFloat(secondaryMile) * 80 + parseFloat(secondaryYard) / 22;

        const distance = Math.abs(secondaryPosition - primaryPosition);
        if (distance < minDistance) {
            minDistance = distance;
            closestIndex = i;
        }
    }

    let secondaryIsUpstream = null;

    let match, matchNext;
    if (closestIndex + 1 < secondaryImages.length) {
        match = secondaryImages[closestIndex];
        matchNext = secondaryImages[closestIndex + 1];
    } else {
        match = secondaryImages[closestIndex - 1];
        matchNext = secondaryImages[closestIndex];
    }

    if (!match) {
        notification.warn({ message: "No positional overlap with current location", key: "secondary-match-warning", duration: 0 });
        return null;
    }

    notification.destroy({ key: "secondary-match-warning" });

    const { mile: secondaryMile, yard: secondaryYard } = match.mwv;
    const { mile: secondaryMileNext, yard: secondaryYardNext } = matchNext.mwv;
    const secondaryPosition = 80 * secondaryMile + secondaryYard / 22;
    const secondaryPositionNext = 80 * secondaryMileNext + secondaryYardNext / 22;

    if (closestIndex !== null) {
        if (closestIndex < secondaryImages.length - 1) {
            secondaryIsUpstream = secondaryPositionNext > secondaryPosition;
        } else if (closestIndex > 0) {
            secondaryIsUpstream = secondaryPosition > secondaryPositionNext;
        }
    }

    const useNext = railImages[centreIndex + 1] ? true : false;
    const nextPrimaryTile = useNext ? railImages[centreIndex + 1] : railImages[centreIndex - 1];

    const nextPrimaryPosition = 80 * parseFloat(nextPrimaryTile.mwv.mile) + parseFloat(nextPrimaryTile.mwv.yard) / 22;

    let primaryIsUpstream = null;
    if (nextPrimaryTile) {
        primaryIsUpstream = useNext ? nextPrimaryPosition > primaryPosition : nextPrimaryPosition < primaryPosition;
    }
    //intra distance should never exceed 1

    const baseDiff = parseFloat(primaryYard) - parseFloat(match.mwv.yard);
    const intraDistance = Math.max(
        -1,
        Math.min(1, (primaryIsUpstream !== null && secondaryIsUpstream !== null && primaryIsUpstream === secondaryIsUpstream ? -baseDiff : baseDiff) * 0.9144),
    );

    if (DEBUG) {
        console.log("Centre index: ", centreIndex, "Closest index: ", closestIndex);
        console.log(currentTile.mwv.yard, " debug inter matched with: ", secondaryImages[closestIndex].mwv.yard, " and added: ", intraDistance);
    }

    return {
        inter: closestIndex - centreIndex,
        intra: intraDistance,
        centreIndex,
        railArrayOffset,
        primaryIsUpstream,
        secondaryIsUpstream,
    };
};

/**
 *
 * @param {number[]} chains
 * @returns {number[]} yards
 */
export const chainsToYards = (chains) => {
    return chains.map((chain) => Math.abs(chain) * 22);
};

/**
 *
 * @param {{ position: {elr: string, trid: string, position: number}; timestamp: number; clickedIndex: number; }} currentImage
 * @param {{ position: {elr: string, trid: string, position: number}; timestamp: number; clickedIndex: number; }} hoveredImage
 * @returns {boolean}
 */
export const imageOutOfRangeForMeasure = (currentImage, currentIndex, hoveredImage, hoveredIndex) => {
    if (!currentImage || !hoveredImage) return true;

    if (Math.abs(currentIndex - hoveredIndex) > 1) {
        console.warn("Measuring between two non consecutive indexes");
        return false;
    }

    const currentPosition = currentImage.position;
    const hoveredPosition = hoveredImage.position;

    const within10Seconds = Math.abs(currentImage.timestamp - hoveredImage.timestamp) < 10000;

    if (!(currentPosition.position && hoveredPosition.position)) {
        console.log("returned in here");
        return within10Seconds;
    }

    const [currentYards, hoveredYards] = chainsToYards([currentPosition.position, hoveredPosition.position]);

    const isSameElrTrid = currentPosition.elr === hoveredPosition.elr && currentPosition.trid === hoveredPosition.trid;

    if (isSameElrTrid) {
        return Math.abs(currentYards - hoveredYards) < 2;
    }

    return true;
};
